// TabsBar — closeable multi-tab strip rendered between AppBar and main
// content. Reference TVP2 §9 image2: each opened module page shows as a
// tab w/ leading icon + label + (when more than one tab) a × close
// button. Active tab carries the teal underline (TabIndicator).

import { Box, Tab, Tabs, IconButton } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { tokens } from "@findgeeks/ui-tokens";
import { getRouteIcon } from "@/lib/routeIcons";
import type { TabRecord } from "@/lib/tabs";

type Props = {
  tabs: TabRecord[];
  activePath: string;
  onTabClick: (path: string) => void;
  onClose: (path: string) => void;
};

export function TabsBar({ tabs, activePath, onTabClick, onClose }: Props) {
  // MUI Tabs requires the `value` to match one of its child <Tab value>s
  // exactly — if the URL is on a sub-route we don't have a tab for, find
  // the closest open-tab ancestor; if none, render with no active tab
  // (false) so MUI doesn't warn about an unknown value.
  const matchedActive =
    tabs.find((t) => t.path === activePath)?.path ??
    tabs.find((t) => activePath.startsWith(t.path))?.path ??
    false;

  return (
    <Box
      sx={{
        // Reference TVP2 §9 image2: tab strip background = page bg
        // (light grey #F5F7FA), NOT pure white. The white card surfaces
        // (search panel, table cards) sit BELOW the tab strip with their
        // own white backgrounds — the strip itself blends into the page.
        background: tokens.colors.admin.bg,
        borderBottom: "none",
      }}
    >
      <Tabs
        value={matchedActive}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Open pages"
        sx={{
          minHeight: 38,
          // Indicator: thin teal underline (reference shows ~2-3px).
          "& .MuiTabs-indicator": {
            backgroundColor: tokens.colors.admin.tabActive,
            height: 2,
          },
          "& .MuiTab-root": {
            minHeight: 38,
            paddingTop: "8px",
            paddingBottom: "8px",
            textTransform: "none",
            fontSize: 13,
            fontWeight: tokens.font.weights.regular,
            // Per user feedback: tab text + icon use the SAME grey as the
            // sidebar nav items (textBody / sidebarText, #424242). The teal
            // underline indicator alone signals selection — no color change
            // on Mui-selected. Icon follows the parent text color.
            color: tokens.colors.admin.textBody,
            "&.Mui-selected": {
              color: tokens.colors.admin.textBody,
              fontWeight: tokens.font.weights.regular,
            },
            "& .MuiTab-iconWrapper svg": {
              color: "inherit",
            },
          },
        }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.path}
            value={tab.path}
            onClick={() => onTabClick(tab.path)}
            iconPosition="start"
            icon={getRouteIcon(tab.iconKey) ?? undefined}
            label={
              <Box
                component="span"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
              >
                <span>{tab.label}</span>
                {tabs.length > 1 && (
                  <IconButton
                    component="span"
                    size="small"
                    aria-label={`Close ${tab.label}`}
                    onClick={(e) => {
                      // Stop the click from also bubbling to the Tab itself
                      // (which would re-select the tab being closed).
                      e.stopPropagation();
                      onClose(tab.path);
                    }}
                    sx={{
                      ml: 0.25,
                      p: 0.25,
                      color: tokens.colors.admin.textMuted,
                      "&:hover": {
                        color: tokens.colors.admin.danger,
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                )}
              </Box>
            }
            sx={{
              px: 1.5,
              "& .MuiTab-iconWrapper": {
                marginRight: 0.5,
                marginBottom: "0 !important",
                "& svg": { fontSize: 16 },
              },
            }}
          />
        ))}
      </Tabs>
    </Box>
  );
}
